import { BrowserProvider, Contract, formatUnits, hexlify, JsonRpcProvider, parseEther, parseUnits, toUtf8Bytes } from 'ethers';

const TOKEN_READ_ABI = [
  'function name() view returns (string)',
  'function symbol() view returns (string)',
  'function decimals() view returns (uint8)',
  'function totalSupply() view returns (uint256)',
];

const CHAIN_RPC = {
  56: 'https://bsc-dataseed.binance.org/',
  97: 'https://data-seed-prebsc-1-s1.binance.org:8545/',
};

/** Public read of the DFI contract already configured for this app. */
export async function readTokenDetails(address, chainId = 56) {
  const id = Number(chainId) || 56;
  const provider = new JsonRpcProvider(CHAIN_RPC[id] || CHAIN_RPC[56], id, { staticNetwork: true });
  try {
    const token = new Contract(address, TOKEN_READ_ABI, provider);
    const [name, symbol, decimals, totalSupply] = await Promise.all([
      token.name(),
      token.symbol(),
      token.decimals(),
      token.totalSupply(),
    ]);
    const places = Number(decimals);
    return {
      name: String(name),
      symbol: String(symbol),
      decimals: places,
      totalSupply: formatUnits(totalSupply, places),
    };
  } finally {
    provider.destroy();
  }
}

const ERC20_ABI = [
  'function transfer(address to, uint256 amount) returns (bool)',
  'function decimals() view returns (uint8)',
  'function balanceOf(address account) view returns (uint256)',
  'function allowance(address owner, address spender) view returns (uint256)',
];

export function hasInjectedProvider() {
  return typeof window !== 'undefined' && !!(window.ethereum || window.trustwallet);
}

function listInjectedProviders() {
  const list = [];
  if (window.ethereum) {
    if (Array.isArray(window.ethereum.providers)) {
      list.push(...window.ethereum.providers);
    } else {
      list.push(window.ethereum);
    }
  }
  // Trust Wallet browser extension sometimes exposes a dedicated object
  if (window.trustwallet && !list.includes(window.trustwallet)) {
    list.push(window.trustwallet);
  }
  return list;
}

function isTrustProvider(p) {
  return !!(p && (p.isTrust || p.isTrustWallet));
}

function isTrustInApp() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /Trust\//i.test(ua) || /TrustWallet/i.test(ua);
}

/** EIP-6963 announcements. Trust's `window.trustwallet` object is often a dead shim. */
const announcedProviders = [];

function rememberAnnouncedProvider(detail) {
  if (!detail?.provider) return;
  const rdns = detail.info?.rdns || '';
  const existing = rdns
    ? announcedProviders.findIndex((item) => item.info?.rdns === rdns)
    : announcedProviders.findIndex((item) => item.provider === detail.provider);
  if (existing >= 0) announcedProviders[existing] = detail;
  else if (!announcedProviders.some((item) => item.provider === detail.provider)) {
    announcedProviders.push(detail);
  }
}

function requestAnnouncedProviders() {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new Event('eip6963:requestProvider'));
  } catch {
    // ignore
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('eip6963:announceProvider', (event) => {
    rememberAnnouncedProvider(event.detail);
  });
  requestAnnouncedProviders();
}

function findAnnouncedProvider(match) {
  requestAnnouncedProviders();
  const hit = announcedProviders.find((detail) => match(detail));
  return hit?.provider || null;
}

function trustAnnouncedProvider() {
  return findAnnouncedProvider((detail) => {
    const rdns = detail.info?.rdns || '';
    return rdns === 'com.trustwallet.app';
  });
}

/**
 * Trust Wallet sets isMetaMask and often replaces window.ethereum.
 * The real MetaMask extension is announced separately as io.metamask.
 */
function isRealMetaMask(provider) {
  return !!(provider && provider.isMetaMask && !provider.isBraveWallet && !isTrustProvider(provider));
}

function metamaskProvider() {
  const announced = findAnnouncedProvider((detail) => {
    const rdns = detail.info?.rdns || '';
    return rdns === 'io.metamask' || rdns === 'io.metamask.flask';
  });
  if (announced) return announced;
  return listInjectedProviders().find(isRealMetaMask) || null;
}

function trustProviderCandidates() {
  const list = [];
  const push = (provider) => {
    if (provider && !list.includes(provider)) list.push(provider);
  };

  // Inside the Trust dApp browser, window.ethereum is the native bridge.
  // window.trustwallet there often posts on a BroadcastChannel that is already closed.
  if (isTrustInApp()) push(window.ethereum);

  push(trustAnnouncedProvider());

  for (const provider of listInjectedProviders()) {
    if (isTrustProvider(provider)) push(provider);
  }

  push(window.trustwallet);

  if (isTrustInApp() && window.ethereum?.providers) {
    window.ethereum.providers.filter(isTrustProvider).forEach(push);
  }

  return list;
}

export function detectWalletAvailability() {
  const providers = listInjectedProviders();
  const any = providers.length > 0;
  return {
    metamask: !!metamaskProvider(),
    trust:
      isTrustInApp() ||
      !!trustAnnouncedProvider() ||
      providers.some(isTrustProvider) ||
      !!window.trustwallet,
    binance: providers.some((p) => p.isBinance || p.isBinanceWallet),
    coinbase: providers.some((p) => p.isCoinbaseWallet || p.isCoinbaseBrowser),
    okx: providers.some((p) => p.isOkxWallet || p.isOKExWallet),
    bitget: providers.some((p) => p.isBitKeep || p.isBitget),
    tokenpocket: providers.some((p) => p.isTokenPocket),
    safepal: providers.some((p) => p.isSafePal),
    injected: any,
  };
}

/**
 * True when the page is running inside a mobile wallet dApp browser
 * (Trust, MetaMask Mobile, TokenPocket, etc.), not a normal desktop browser.
 */
export function isInAppDappBrowser() {
  if (typeof window === 'undefined') return false;
  return detectInAppDappWallet() != null;
}

/**
 * Detect wallet in-app dApp browsers (Trust, MetaMask Mobile, etc.).
 * Returns a wallet id to auto-connect, or null for normal browsers
 * where the wallet picker should be shown.
 */
export function detectInAppDappWallet() {
  if (typeof window === 'undefined') return null;

  const ua = navigator.userAgent || '';
  const eth = window.ethereum;
  const providers = listInjectedProviders();
  const isMobile = /Android|iPhone|iPad|iPod/i.test(ua);

  // UA is the strongest signal — check before provider-count heuristics.
  // Trust mobile dApp browser typically includes "Trust/" or "TrustWallet".
  if (/Trust\//i.test(ua) || /TrustWallet/i.test(ua)) return 'trust';
  if (/MetaMaskMobile/i.test(ua)) return 'metamask';
  if (/TokenPocket/i.test(ua)) return 'tokenpocket';
  if (/CoinbaseWallet|CBWallet/i.test(ua)) return 'coinbase';
  if (/OKApp/i.test(ua) || (/OKX/i.test(ua) && isMobile)) return 'okx';
  if (/BitKeep|Bitget/i.test(ua)) return 'bitget';
  if (/SafePal/i.test(ua)) return 'safepal';
  if (/Binance/i.test(ua) && isMobile) return 'binance';

  // Desktop browser with extensions → always show the picker
  const isDesktopBrowser =
    /Windows|Macintosh|Linux/i.test(ua) &&
    !isMobile &&
    !/MetaMaskMobile|Trust\/|TokenPocket|CoinbaseWallet|OKApp|BitKeep|Bitget|SafePal/i.test(ua);
  if (isDesktopBrowser) return null;

  // Mobile / WebView with Trust provider flags (ethereum + trustwallet both present is common)
  if (isMobile || /Mobile|WebView/i.test(ua)) {
    if (isTrustProvider(eth) || isTrustProvider(window.trustwallet) || providers.some(isTrustProvider)) {
      return 'trust';
    }
    if (eth?.isMetaMask && !eth.isBraveWallet && !isTrustProvider(eth)) return 'metamask';
    if (eth?.isCoinbaseWallet || eth?.isCoinbaseBrowser) return 'coinbase';
    if (eth?.isOkxWallet || eth?.isOKExWallet) return 'okx';
    if (eth?.isBitKeep || eth?.isBitget) return 'bitget';
    if (eth?.isTokenPocket) return 'tokenpocket';
    if (eth?.isSafePal) return 'safepal';
    if (eth?.isBinance || eth?.isBinanceWallet) return 'binance';

    // Single injected provider on mobile = in-app browser
    if (providers.length === 1) return 'injected';
    // Multiple providers but clearly mobile with injection — use primary
    if (providers.length > 0 && isMobile) return 'injected';
  }

  return null;
}

/**
 * Wait briefly for Trust/MetaMask to inject `window.ethereum` (can lag on first load).
 */
export async function waitForInAppDappWallet(timeoutMs = 2500) {
  const deadline = Date.now() + timeoutMs;
  let id = detectInAppDappWallet();
  if (id) return id;

  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 120));
    id = detectInAppDappWallet();
    if (id) return id;
    // Mobile UA + any injection that appeared late
    const ua = navigator.userAgent || '';
    if (
      /Android|iPhone|iPad|iPod|Trust\/|TrustWallet|MetaMaskMobile/i.test(ua) &&
      hasInjectedProvider()
    ) {
      return detectInAppDappWallet() || 'injected';
    }
  }
  return detectInAppDappWallet();
}

function announcedByNeedle(needles) {
  return findAnnouncedProvider((detail) => {
    const rdns = String(detail.info?.rdns || '').toLowerCase();
    const name = String(detail.info?.name || '').toLowerCase();
    return needles.some((needle) => rdns === needle || rdns.includes(needle) || name.includes(needle));
  });
}

function pickProvider(walletId) {
  if (walletId === 'metamask') return metamaskProvider();
  if (walletId === 'trust') return trustProviderCandidates()[0] || null;

  const announcedNeedles = {
    binance: ['binance'],
    coinbase: ['coinbase'],
    okx: ['okex', 'okx'],
    bitget: ['bitget', 'bitkeep'],
    tokenpocket: ['tokenpocket'],
    safepal: ['safepal'],
  };
  if (announcedNeedles[walletId]) {
    const announced = announcedByNeedle(announcedNeedles[walletId]);
    if (announced) return announced;
  }

  const providers = listInjectedProviders();
  if (!providers.length) return null;

  const matchers = {
    binance: (p) => p.isBinance || p.isBinanceWallet,
    coinbase: (p) => p.isCoinbaseWallet || p.isCoinbaseBrowser,
    okx: (p) => p.isOkxWallet || p.isOKExWallet,
    bitget: (p) => p.isBitKeep || p.isBitget,
    tokenpocket: (p) => p.isTokenPocket,
    safepal: (p) => p.isSafePal,
    injected: () => true,
  };

  const matcher = matchers[walletId] || matchers.injected;
  const found = providers.find(matcher);
  if (found) return found;
  if (walletId === 'injected') return providers[0];
  return null;
}

function openInstallLink(walletId) {
  const path = window.location.pathname;
  const dappUrl = encodeURIComponent(`${window.location.origin}${path}`);
  const links = {
    metamask: `https://metamask.app.link/dapp/${window.location.host}${path}`,
    trust: `https://link.trustwallet.com/open_url?coin_id=20000714&url=${dappUrl}`,
    binance: 'https://www.bnbchain.org/en/wallets',
    coinbase: 'https://www.coinbase.com/wallet',
    okx: 'https://www.okx.com/download',
    bitget: 'https://web3.bitget.com/en/wallet-download',
    tokenpocket: 'https://www.tokenpocket.pro/en/download/app',
    safepal: 'https://www.safepal.com/download',
  };
  const url = links[walletId];
  if (url) window.open(url, '_blank', 'noopener,noreferrer');
}

/** Unwrap ethers “could not coalesce error” into a usable message. */
function normalizeProviderError(err) {
  if (!err) return new Error('Wallet request failed');

  const nested =
    err.error ||
    err.info?.error ||
    err.data ||
    (typeof err.shortMessage === 'string' ? { message: err.shortMessage } : null);

  const message =
    nested?.message ||
    err.reason ||
    err.shortMessage ||
    err.message ||
    'Wallet request failed';

  const code = nested?.code ?? err.code;
  const out = new Error(String(message));
  out.code = code;
  out.cause = err;
  return out;
}

function isBroadcastChannelError(err) {
  const msg = [
    err?.message,
    err?.shortMessage,
    err?.reason,
    err?.data?.message,
    err?.error?.message,
    err?.cause?.message,
    typeof err === 'string' ? err : '',
  ]
    .join(' ')
    .toLowerCase();
  return msg.includes('broadcast channel') || msg.includes('broadcastchannel');
}

const WRAPPED = Symbol('dynafiWrapped');
let activeEthereum = null;
let connectedWalletId = 'injected';

/**
 * Trust's EIP-1193 request() talks over a BroadcastChannel that is often already
 * closed inside the app browser. enable() / sendAsync still use the native bridge.
 */
function legacyRpc(provider, method, params = []) {
  const payload = { jsonrpc: '2.0', id: Date.now() % 1e9, method, params };

  const viaCallback = (fn) =>
    new Promise((resolve, reject) => {
      let settled = false;
      const finish = (err, response) => {
        if (settled) return;
        settled = true;
        if (err) {
          reject(normalizeProviderError(err));
          return;
        }
        const error = response?.error;
        if (error) {
          reject(normalizeProviderError(error));
          return;
        }
        if (response && Object.prototype.hasOwnProperty.call(response, 'result')) {
          resolve(response.result);
          return;
        }
        resolve(response);
      };
      try {
        const ret = fn(payload, finish);
        if (ret && typeof ret.then === 'function') {
          ret.then((res) => finish(null, res), (err) => finish(err));
        } else if (ret !== undefined) {
          finish(null, ret);
        }
      } catch (err) {
        finish(err);
      }
    });

  return (async () => {
    if (method === 'eth_requestAccounts' && typeof provider.enable === 'function') {
      try {
        const accounts = await provider.enable();
        if (accounts?.length) return accounts;
      } catch (err) {
        const normalized = normalizeProviderError(err);
        if (!isBroadcastChannelError(normalized) && normalized.code !== -32601) throw normalized;
      }
    }

    if (typeof provider.sendAsync === 'function') {
      try {
        return await viaCallback((body, cb) => provider.sendAsync(body, cb));
      } catch (err) {
        const normalized = normalizeProviderError(err);
        if (!isBroadcastChannelError(normalized)) throw normalized;
      }
    }

    if (typeof provider.send === 'function') {
      return viaCallback((body, cb) => provider.send(body, cb));
    }

    throw new Error('Wallet request failed');
  })();
}

function withLegacyFallback(provider) {
  if (!provider || provider[WRAPPED]) return provider;
  return new Proxy(provider, {
    get(target, prop, receiver) {
      if (prop === WRAPPED) return true;
      if (prop === 'request') {
        return async (args) => {
          const method = args?.method;
          const params = args?.params || [];
          if (typeof target.request === 'function') {
            try {
              return await target.request({ method, params });
            } catch (err) {
              const normalized = normalizeProviderError(err);
              if (!isBroadcastChannelError(normalized)) throw normalized;
            }
          }
          return legacyRpc(target, method, params);
        };
      }
      const value = Reflect.get(target, prop, receiver);
      return typeof value === 'function' ? value.bind(target) : value;
    },
  });
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isUserRejection(err) {
  const code = err?.code;
  const message = String(err?.message || '');
  return code === 4001 || /user rejected|user denied|denied by the user|cancelled|canceled/i.test(message);
}

function normalizeAddressList(list) {
  if (!Array.isArray(list)) return [];
  return list.map((item) => String(item || '').toLowerCase()).filter(Boolean);
}

async function readEthAccounts(ethereum) {
  try {
    return normalizeAddressList(await ethereum.request({ method: 'eth_accounts' }));
  } catch {
    return [];
  }
}

async function waitUntilAccountsClear(ethereum, timeoutMs = 700) {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    const accounts = await readEthAccounts(ethereum);
    if (!accounts.length) return true;
    await sleep(80);
  }
  return false;
}

async function revokeAccountPermission(ethereum) {
  if (typeof ethereum?.request !== 'function') return false;
  const attempts = [
    [{ eth_accounts: {} }],
    ['eth_accounts'],
  ];
  let revoked = false;
  for (const params of attempts) {
    try {
      await ethereum.request({
        method: 'wallet_revokePermissions',
        params,
      });
      revoked = true;
      break;
    } catch (err) {
      const normalized = normalizeProviderError(err);
      if (isUserRejection(normalized)) throw friendlyConnectError(normalized);
    }
  }
  if (typeof ethereum.disconnect === 'function') {
    try {
      await ethereum.disconnect();
      revoked = true;
    } catch {
      // some injected providers expose disconnect() without using it
    }
  }
  if (!revoked) return false;
  return waitUntilAccountsClear(ethereum);
}

async function confirmConnectInWallet(ethereum, address) {
  const message = [
    'DynaFi wants to connect to your wallet.',
    '',
    'This is not a payment. Confirm in your wallet to allow the connection.',
    `Time: ${new Date().toISOString()}`,
  ].join('\n');
  const data = hexlify(toUtf8Bytes(message));
  const from = String(address);
  try {
    await ethereum.request({
      method: 'personal_sign',
      params: [data, from],
    });
    return true;
  } catch (err) {
    const first = normalizeProviderError(err);
    if (isUserRejection(first)) throw friendlyConnectError(first);
    try {
      await ethereum.request({
        method: 'personal_sign',
        params: [from, data],
      });
      return true;
    } catch (retryErr) {
      throw friendlyConnectError(normalizeProviderError(retryErr));
    }
  }
}

async function askEthRequestAccounts(ethereum) {
  try {
    const accounts = normalizeAddressList(await ethereum.request({ method: 'eth_requestAccounts' }));
    if (accounts.length) return accounts;
  } catch (err) {
    const normalized = normalizeProviderError(err);
    if (isUserRejection(normalized)) throw friendlyConnectError(normalized);
    if (normalized.code === -32002) {
      await sleep(400);
      const retry = normalizeAddressList(await ethereum.request({ method: 'eth_requestAccounts' }));
      if (retry.length) return retry;
    } else if (isBroadcastChannelError(normalized)) {
      throw friendlyConnectError(normalized);
    } else {
      throw friendlyConnectError(normalized);
    }
  }
  return [];
}

/**
 * eth_requestAccounts is silent while the origin is still approved in the
 * extension. Drop that approval first; if the wallet ignores revoke, require
 * an explicit confirm in the wallet app.
 */
async function requestAccountsWithConsent(ethereum) {
  const before = await readEthAccounts(ethereum);
  if (before.length) {
    const cleared = await revokeAccountPermission(ethereum);
    const remaining = await readEthAccounts(ethereum);
    if (!cleared && remaining.length) {
      await confirmConnectInWallet(ethereum, remaining[0]);
      return remaining;
    }
  }

  const requested = await askEthRequestAccounts(ethereum);
  if (requested.length) return requested;
  throw new Error('No account selected in wallet');
}

/** Drop the site connection in the wallet so the next connect can prompt again. */
export async function disconnectWallet() {
  const ethereum = activeEthereum || (typeof window !== 'undefined' ? window.ethereum : null);
  connectedWalletId = 'injected';
  activeEthereum = null;
  if (!ethereum) return;
  try {
    await revokeAccountPermission(ethereum);
  } catch (err) {
    if (err?.code === 4001) return;
  }
}

async function requestAccounts(ethereum, { forcePermission = false } = {}) {
  if (typeof ethereum.request !== 'function') {
    throw new Error('Wallet provider does not support eth_requestAccounts');
  }
  if (forcePermission) {
    return requestAccountsWithConsent(ethereum);
  }

  const accounts = await askEthRequestAccounts(ethereum);
  if (accounts.length) return accounts;
  const existing = await readEthAccounts(ethereum);
  if (existing.length) return existing;
  throw new Error('No account selected in wallet');
}

function friendlyConnectError(err) {
  if (isBroadcastChannelError(err)) {
    const out = new Error('Trust Wallet did not open. Unlock the extension and try again.');
    out.code = 'BROADCAST_CHANNEL';
    return out;
  }
  if (err.code === 4001 || /user rejected|denied/i.test(err.message || '')) {
    const out = new Error('Connection request was rejected in the wallet');
    out.code = 4001;
    return out;
  }
  return err instanceof Error ? err : new Error(String(err.message || err));
}

async function ensureBsc(ethereum) {
  const chainId = Number(process.env.REACT_APP_CHAIN_ID || 56);
  const wantedHex = `0x${chainId.toString(16)}`;

  let currentHex;
  try {
    currentHex = await ethereum.request({ method: 'eth_chainId' });
  } catch (err) {
    throw friendlyConnectError(normalizeProviderError(err));
  }

  if (String(currentHex).toLowerCase() === wantedHex.toLowerCase()) {
    return new BrowserProvider(ethereum);
  }

  try {
    await ethereum.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: wantedHex }],
    });
  } catch (e) {
    const err = normalizeProviderError(e);
    if (err.code === 4902 || /unrecognized chain|unknown chain/i.test(err.message || '')) {
      try {
        await ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [
            {
              chainId: wantedHex,
              chainName: 'BNB Smart Chain',
              nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
              rpcUrls: ['https://bsc-dataseed.binance.org/'],
              blockExplorerUrls: ['https://bscscan.com'],
            },
          ],
        });
      } catch (addErr) {
        throw friendlyConnectError(normalizeProviderError(addErr));
      }
    } else if (err.code === 4001) {
      const rejected = new Error('Network switch was rejected in the wallet');
      rejected.code = 4001;
      throw rejected;
    } else {
      throw friendlyConnectError(err);
    }
  }

  return new BrowserProvider(ethereum);
}

const TRUST_BRIDGE_KEY = 'dynafi_trust_bridge_retry';
let trustResumeTask = null;

function isTrustBridgeDown(err) {
  return String(err?.message || '').toLowerCase().includes('broadcast channel unavailable');
}

export function beginTrustBridgeResume(start) {
  let pending = false;
  try {
    pending = sessionStorage.getItem(TRUST_BRIDGE_KEY) === 'reload';
  } catch {
    return null;
  }
  if (!trustResumeTask && !pending) return null;
  if (!trustResumeTask) {
    try { sessionStorage.setItem(TRUST_BRIDGE_KEY, 'done'); } catch { /* ignore */ }
    trustResumeTask = Promise.resolve().then(start);
  }
  return trustResumeTask;
}

async function requestTrustAccounts(ethereum, { forcePermission = false } = {}) {
  if (typeof ethereum.request !== 'function') {
    throw new Error('Trust Wallet provider does not support eth_requestAccounts');
  }

  let lastError = null;
  try {
    const accounts = forcePermission
      ? await requestAccountsWithConsent(ethereum)
      : await requestAccounts(ethereum, { forcePermission: false });
    if (accounts?.length) {
      try { sessionStorage.removeItem(TRUST_BRIDGE_KEY); } catch { /* ignore */ }
      return accounts;
    }
  } catch (err) {
    const normalized = err instanceof Error ? err : normalizeProviderError(err);
    if (normalized.code === 4001 || isUserRejection(normalized)) throw friendlyConnectError(normalized);
    if (
      normalized.code !== 'BROADCAST_CHANNEL' &&
      !isTrustBridgeDown(normalized) &&
      !isBroadcastChannelError(normalized)
    ) {
      throw normalized;
    }
    lastError = normalized;
  }

  let alreadyReloaded = false;
  try {
    alreadyReloaded = sessionStorage.getItem(TRUST_BRIDGE_KEY) === 'done';
    if (!alreadyReloaded) sessionStorage.setItem(TRUST_BRIDGE_KEY, 'reload');
  } catch {
    alreadyReloaded = true;
  }
  if (!alreadyReloaded) {
    window.location.reload();
    return new Promise(() => {});
  }
  try { sessionStorage.removeItem(TRUST_BRIDGE_KEY); } catch { /* ignore */ }
  const failed = new Error('Trust Wallet could not open its connect window. Update the Trust Wallet extension, then refresh and try again.');
  failed.cause = lastError;
  throw failed;
}

async function connectWithProvider(raw, walletId, options = {}) {
  // Trust's extension opens its prompt from request(). enable()/sendAsync()
  // call request() again and the prompt never stays on screen.
  const directTrust = walletId === 'trust' && !isTrustInApp();
  const ethereum = directTrust ? raw : withLegacyFallback(raw);
  const accounts = directTrust
    ? await requestTrustAccounts(ethereum, options)
    : await requestAccounts(ethereum, options);
  if (!accounts?.length) {
    throw new Error('No account selected in wallet');
  }

  const provider = await ensureBsc(ethereum);
  const signer = await provider.getSigner(accounts[0]);
  const address = (await signer.getAddress()).toLowerCase();
  activeEthereum = ethereum;
  connectedWalletId = walletId;
  return { provider, signer, address, walletId, ethereum };
}

function trustConnectProvider() {
  if (isTrustInApp() && window.ethereum) return window.ethereum;
  return (
    trustAnnouncedProvider() ||
    (typeof window.trustwallet?.request === 'function' ? window.trustwallet : null) ||
    trustProviderCandidates()[0] ||
    null
  );
}

export async function connectWallet(walletId = 'injected', options = {}) {
  const candidates =
    walletId === 'trust' ? [trustConnectProvider()].filter(Boolean) : [pickProvider(walletId)].filter(Boolean);

  if (!candidates.length) {
    openInstallLink(walletId);
    throw new Error(
      `No ${walletId} provider found. Install the wallet extension or open this dApp inside the wallet browser.`
    );
  }

  let lastError = null;
  for (const raw of candidates) {
    try {
      return await connectWithProvider(raw, walletId, options);
    } catch (err) {
      const normalized = err instanceof Error ? err : normalizeProviderError(err);
      if (normalized.code === 4001) throw normalized;
      lastError = normalized;
    }
  }

  throw lastError || new Error('Wallet request failed');
}

function paymentErrorText(err) {
  const parts = [];
  const seen = new Set();
  const walk = (node, depth) => {
    if (node == null || depth > 5) return;
    if (typeof node === 'string') {
      parts.push(node);
      return;
    }
    if (typeof node !== 'object' || seen.has(node)) return;
    seen.add(node);
    parts.push(node.code, node.message, node.shortMessage, node.reason);
    walk(node.error, depth + 1);
    walk(node.info, depth + 1);
    walk(node.data, depth + 1);
    walk(node.cause, depth + 1);
  };
  walk(err, 0);
  return parts.filter((part) => part != null && part !== '').join(' ');
}

/** Wallet and RPC failures shown to the person paying, never the raw provider payload. */
export function friendlyPaymentError(err, symbol = 'USDT') {
  const text = paymentErrorText(err);
  const code = err?.code ?? err?.error?.code ?? err?.info?.error?.code;
  const cancelled =
    code === 4001 ||
    code === 'ACTION_REJECTED' ||
    /\b4001\b|ACTION_REJECTED/.test(text) ||
    /user rejected|user denied|rejected the request|denied transaction|action[_\s-]?rejected|request rejected/i.test(text) ||
    (/could not coalesce/i.test(text) && /eth_sendTransaction/i.test(text) && /"message"\s*:\s*""/.test(text));

  if (cancelled) {
    const out = new Error(`Payment cancelled. No ${symbol} was sent.`);
    out.code = 4001;
    return out;
  }
  if (/insufficient funds for gas|intrinsic gas too low|gas required exceeds/i.test(text)) {
    return new Error('Your wallet needs a little BNB to pay the network fee.');
  }
  if (/transfer amount exceeds balance|exceeds balance|insufficient usdt|insufficient balance/i.test(text)) {
    return new Error(`Your wallet does not have enough ${symbol} for this amount.`);
  }
  if (/chain.?id|wrong network|network mismatch|unsupported chain/i.test(text)) {
    return new Error('Switch your wallet to BNB Smart Chain and try again.');
  }
  if (/nonce too low|replacement transaction underpriced|already known/i.test(text)) {
    return new Error('A previous transfer is still pending in your wallet. Wait for it to finish, then try again.');
  }
  if (/could not coalesce|UNKNOWN_ERROR|eth_sendTransaction|jsonrpc|CALL_EXCEPTION|missing revert data/i.test(text)) {
    return new Error(`The payment could not be sent. Check your ${symbol} balance and try again.`);
  }

  const short = String(err?.shortMessage || err?.reason || err?.message || '').trim();
  if (short && short.length < 140 && !/0x[a-fA-F0-9]{16,}|payload=|jsonrpc/i.test(short)) {
    return err instanceof Error && err.message === short ? err : new Error(short);
  }
  return new Error('The payment could not be sent. Please try again.');
}

/**
 * Transfer an 18-decimal BEP-20 token. `onSubmitted(hash)` fires as soon as the wallet broadcasts,
 * before the receipt, so the server can start verifying even if the page closes while mining.
 */
export async function sendToken({ tokenContract, to, amount, symbol = 'USDT', onSubmitted }) {
  try {
    const { signer, address } = await connectWallet(connectedWalletId || 'injected');
    const token = new Contract(tokenContract, ERC20_ABI, signer);
    const value = parseUnits(String(amount), TOKEN_DECIMALS);
    const tx = await token.transfer(to, value);
    if (onSubmitted) await Promise.resolve(onSubmitted(tx.hash)).catch(() => {});
    const receipt = await tx.wait();
    return { txHash: receipt.hash, from: address };
  } catch (err) {
    throw friendlyPaymentError(err, symbol);
  }
}

export async function sendUsdt({ usdtContract, merchantWallet, amount }) {
  return sendToken({ tokenContract: usdtContract, to: merchantWallet, amount });
}

export async function sendBnb({ to, amount, onSubmitted }) {
  try {
    const { signer, address } = await connectWallet(connectedWalletId || 'injected');
    const value = parseEther(String(amount));
    const tx = await signer.sendTransaction({ to, value });
    if (onSubmitted) await Promise.resolve(onSubmitted(tx.hash)).catch(() => {});
    const receipt = await tx.wait();
    return { txHash: receipt.hash, from: address };
  } catch (err) {
    throw friendlyPaymentError(err, 'BNB');
  }
}

const TOKEN_DECIMALS = 18;

function bscReadProvider() {
  const id = Number(process.env.REACT_APP_CHAIN_ID || 56);
  return new JsonRpcProvider(CHAIN_RPC[id] || CHAIN_RPC[56], id, { staticNetwork: true });
}

async function readBalance(provider, tokenAddress, ownerAddress) {
  const token = new Contract(tokenAddress, ['function balanceOf(address) view returns (uint256)'], provider);
  const raw = await token.balanceOf(ownerAddress);
  const formatted = formatUnits(raw, TOKEN_DECIMALS);
  return { balance: Number(formatted), formatted, decimals: TOKEN_DECIMALS };
}

export async function getErc20Balance(tokenAddress, ownerAddress) {
  if (!tokenAddress || !ownerAddress) {
    return { balance: 0, formatted: '0', decimals: TOKEN_DECIMALS };
  }

  const provider = bscReadProvider();
  try {
    return await readBalance(provider, tokenAddress, ownerAddress);
  } catch (rpcError) {
    const ethereum = activeEthereum || pickProvider('injected') || (typeof window !== 'undefined' ? window.ethereum : null);
    if (!ethereum) throw rpcError;
    const walletProvider = new BrowserProvider(withLegacyFallback(ethereum));
    return await readBalance(walletProvider, tokenAddress, ownerAddress);
  } finally {
    provider.destroy();
  }
}

export async function getWalletTokenBalances({ usdtContract, dfiContract, ownerAddress }) {
  const [usdt, dfi] = await Promise.all([
    getErc20Balance(usdtContract, ownerAddress),
    getErc20Balance(dfiContract, ownerAddress),
  ]);
  return { usdt, dfi };
}

export function shortAddress(addr) {
  if (!addr) return '';
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}
